/**
 * Disposition — die Gesamtübersicht aller Touren.
 *
 * **Aller** Touren: die vom Büro geplanten und die, die ein Fahrer unterwegs
 * selbst angelegt hat. Zwei getrennte Listen wären zwei Orte, an denen man
 * nachsehen muss, und einer davon würde vergessen. Woher eine Tour stammt,
 * steht in einer eigenen Spalte und lässt sich filtern.
 *
 * **Keine Statusknöpfe.** Es gibt hier nichts zu „disponieren" und nichts zu
 * „starten". Man trägt eine Tour ein, und danach sagt die Zeile von selbst,
 * wo sie steht: Der Tag kommt („Geplant"), der Tag ist da und der Fahrer ist
 * noch dran („Tour offen"), gefahren aber ohne Papiere („Belege fehlen"),
 * gefahren und vollständig („Belege verfügbar" — jetzt kann abgerechnet
 * werden), fertig („Abgerechnet").
 *
 * **Jede Spalte filtert.** Die Frage im Büro lautet selten „wo kommt
 * Moosbeuren vor", sondern „welche Touren von Martin am Donnerstag sind noch
 * offen" — drei Einschränkungen gleichzeitig, jede in ihrer Spalte.
 *
 * Ein Klick auf die Zeile führt in die Detailsicht. Alles, was nicht in eine
 * Tabellenzelle passt — Dokumente, Notiz, Verlauf —, steht dort.
 *
 * ═══ EINE EINZIGE HANDLUNG STEHT IN DER LISTE ═══
 *
 * Die Freigabe zur Abrechnung. Sie ist die Ausnahme von „keine Statusknöpfe",
 * und zwar aus einem Grund: Sie wird nicht je Tour getroffen, sondern
 * reihenweise — freitags sitzt jemand vor der Liste und geht durch, was diese
 * Woche auf die Rechnung darf. Dafür jedes Mal in die Detailsicht und zurück zu
 * gehen sind zwei Klicks und ein Seitenwechsel je Tour; bei zwanzig Touren ist
 * das der Unterschied zwischen „gemacht" und „mache ich später".
 *
 * Alles andere bleibt, wo es hingehört: Die Zeile sagt, wo eine Tour steht;
 * geändert wird sie in der Detailsicht.
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Seitenkopf } from '../Rahmen';
import {
  Fahrernotizzeichen,
  Fehlerkasten,
  Freigabeinhalt,
  Karte,
  Knopf,
  Ladehinweis,
  Marker,
  Rueckfrage,
} from '../gestaltung/bausteine';
import { Filtertabelle, type Spalte } from '../gestaltung/Filtertabelle';
import { nummerSortierwert } from '../daten/sortierung';
import { useAbfrage } from '../daten/abfrage';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import {
  abrechnungFreigabeZuruecknehmen,
  abrechnungFreigeben,
  beleglageText,
  betrag,
  euro,
  fahrernotiz,
  freigabeFrage,
  PREISARTTEXT,
  summeAufCent,
  uebersichtLaden,
  ZUSTANDFARBE,
  ZUSTANDTEXT,
  type Auftragszeile,
  type Freigabefrage,
  type Zustand,
} from '../daten/auftraege';
import { AuftragAnlegen } from './AuftragAnlegen';
import { useFormularOffen } from '../daten/formularEntwurf';

const HERKUNFTTEXT: Record<string, string> = {
  buero: 'Büro',
  fahrer: 'Fahrer',
};

const ARTTEXT: Record<string, string> = {
  standard: 'Standard',
  pendel: 'Pendel',
  fremdvergabe: 'Fremdvergabe',
  sonstige: 'Sonstige',
};

function datumKurz(wert: string | null): string {
  if (!wert) return '';
  return new Date(wert).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

export function DispositionSeite() {
  const liste = useAbfrage(uebersichtLaden, []);
  // Offen bleibt offen — auch über Seitenwechsel und Neuladen (HJ-025).
  const [formularOffen, setFormularOffen] = useFormularOffen('neue-tour');
  const gehZu = useNavigate();

  /** Die Tour, zu der gerade nachgefragt wird — was ihr fehlt und was der Fahrer notiert hat. */
  const [frage, setFrage] = useState<{
    auftrag: Auftragszeile;
    inhalt: Freigabefrage;
  } | null>(null);
  /** Die Kennung der Tour, an der gerade gearbeitet wird. */
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Klarfehler | null>(null);

  async function mitFehlern(auftragId: string, tun: () => Promise<void>) {
    setFehler(null);
    setLaeuft(auftragId);
    try {
      await tun();
      liste.auffrischen();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
    } finally {
      setLaeuft(null);
    }
  }

  function freigabeAnstossen(auftrag: Auftragszeile) {
    const inhalt = freigabeFrage(auftrag);
    // Nachgefragt wird nur, wenn es etwas zu sagen gibt: etwas fehlt, ODER der
    // Fahrer hat etwas notiert. Eine Rückfrage, die bei jeder Freigabe
    // erscheint, wird beim vierten Mal blind weggeklickt — und schützt dann vor
    // gar nichts mehr. Die Notiz gehört hierher, weil an dieser Stelle über den
    // Preis entschieden wird; eine Standzeit ist dann noch zu berücksichtigen.
    if (inhalt) {
      setFrage({ auftrag, inhalt });
      return;
    }
    void mitFehlern(auftrag.id, () => abrechnungFreigeben(auftrag.id));
  }

  /**
   * Die Rückfrage steht über der Tabelle — wer weit unten geklickt hat, sieht
   * sie sonst nicht und hält den Knopf für kaputt. Deshalb scrollt sie sich
   * beim Erscheinen einmal selbst in den Blick; `useCallback` sorgt dafür, dass
   * das beim Einhängen passiert und nicht bei jedem Neuzeichnen.
   */
  const inDenBlick = useCallback((element: HTMLDivElement | null) => {
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, []);

  const spalten: Spalte<Auftragszeile>[] = [
    // Vorn, weil die erste Spalte beim Seitwärts-Schieben stehen bleibt (HJ-041):
    // Dann muss zu sehen sein, welche Tour das ist.
    {
      schluessel: 'nummer',
      text: 'Nummer',
      wert: (a) => a.nummer ?? '',
      sortierwert: (a) => nummerSortierwert(a.nummer),
      zeige: (a) => (
        <>
          {a.nummer ? (
            <strong>{a.nummer}</strong>
          ) : (
            <span className="stumm">folgt</span>
          )}
          {/* Damit eine Notiz nicht erst bei der Freigabe auffällt. Als Zeichen,
              nicht als Text — die Zeilenhöhe bleibt, wie sie ist. */}
          {fahrernotiz(a) && <Fahrernotizzeichen text={fahrernotiz(a) ?? ''} />}
        </>
      ),
    },
    {
      schluessel: 'zustand',
      text: 'Zustand',
      auswahl: true,
      wert: (a) => ZUSTANDTEXT[a.zustand] ?? a.zustand,
      zeige: (a) => (
        <Marker art={ZUSTANDFARBE[a.zustand as Zustand] ?? 'neutral'}>
          {ZUSTANDTEXT[a.zustand] ?? a.zustand}
        </Marker>
      ),
    },
    {
      // ═══ WARUM DIESER KNOPF IN DER LISTE STEHT ═══
      //
      // Freigegeben wird reihenweise, nicht einzeln. Der Weg über die
      // Detailsicht kostet je Tour zwei Klicks und einen Seitenwechsel — und
      // danach steht man wieder am Anfang der Liste, statt in der Zeile, in
      // der man war.
      //
      // Filterbar ist die Spalte auch: „nur offene" zeigt genau die Zeilen, um
      // die es freitags geht.
      schluessel: 'freigabe',
      text: 'Abrechnung',
      auswahl: true,
      nichtKuerzen: true,
      wert: (a) =>
        a.rechnungsnummer !== null
          ? 'abgerechnet'
          : a.zustand === 'storniert'
            ? 'storniert'
            : a.abrechnung_freigegeben
              ? 'freigegeben'
              : 'offen',
      zeige: (a) => {
        if (a.rechnungsnummer !== null) {
          return <span className="stumm">abgerechnet</span>;
        }
        if (a.zustand === 'storniert') {
          return <span className="stumm">storniert</span>;
        }
        return (
          // Der Klick auf den Knopf darf nicht zusätzlich die Zeile öffnen —
          // sonst landet man nach jeder Freigabe in der Detailsicht.
          <span onClick={(e) => e.stopPropagation()}>
            {a.abrechnung_freigegeben ? (
              <Knopf
                art="zweit"
                klein
                disabled={laeuft === a.id}
                title="Die Tour verschwindet damit wieder aus „Aufträge abrechnen“."
                onClick={() =>
                  void mitFehlern(a.id, () =>
                    abrechnungFreigabeZuruecknehmen(a.id),
                  )
                }
              >
                Zurücknehmen
              </Knopf>
            ) : (
              <Knopf
                art="haupt"
                klein
                disabled={laeuft === a.id}
                title="Stellt die Tour in „Aufträge abrechnen“."
                onClick={() => freigabeAnstossen(a)}
              >
                Freigeben
              </Knopf>
            )}
          </span>
        );
      },
    },
    {
      schluessel: 'ladedatum',
      text: 'Ladedatum',
      wert: (a) => datumKurz(a.ladedatum),
      // Angezeigt wird „Mo., 24.08.26" — alphabetisch hieße das: nach
      // Wochentag. Sortiert wird nach dem Tag selbst (H1.5).
      sortierwert: (a) => a.ladedatum,
      zeige: (a) =>
        a.ladedatum ? datumKurz(a.ladedatum) : <span className="stumm">—</span>,
    },
    { schluessel: 'kunde', text: 'Kunde', auswahl: true, wert: (a) => a.kunde ?? '' },
    { schluessel: 'ladeort', text: 'Von', auswahl: true, wert: (a) => a.ladeort ?? '' },
    {
      schluessel: 'entladeort',
      text: 'Nach',
      auswahl: true,
      wert: (a) => a.entladeort ?? '',
    },
    { schluessel: 'material', text: 'Material', wert: (a) => a.material ?? '' },
    {
      // Die Beleglage bleibt auch nach einer Freigabe ehrlich: Sie sagt, was
      // fehlt. Dass es trotzdem in Ordnung ist, steht als eigene Marke daneben
      // — nicht anstelle der Auskunft.
      schluessel: 'belege',
      text: 'Belege',
      auswahl: true,
      wert: (a) =>
        a.belege_freigegeben
          ? 'freigegeben'
          : beleglageText(a.beleglage).text,
      zeige: (a) => {
        const lage = beleglageText(a.beleglage);
        if (a.belege_freigegeben) {
          return (
            <Marker art="gut">freigegeben</Marker>
          );
        }
        return <span className={lage.art === 'warn' ? undefined : 'stumm'}>{lage.text}</span>;
      },
    },
    { schluessel: 'fahrer', text: 'Fahrer', auswahl: true, wert: (a) => a.fahrer },
    { schluessel: 'fahrzeug', text: 'Fahrzeug', auswahl: true, wert: (a) => a.fahrzeug },
    {
      schluessel: 'fuhren',
      text: 'Fuhren',
      rechts: true,
      zahl: true,
      wert: (a) => String(a.fuhren),
      zeige: (a) =>
        a.fuhren > 0 ? a.fuhren : <span className="stumm">—</span>,
    },
    {
      schluessel: 'art',
      text: 'Art',
      auswahl: true,
      wert: (a) => ARTTEXT[a.art] ?? a.art,
    },
    {
      schluessel: 'preisart',
      text: 'Preisart',
      auswahl: true,
      wert: (a) => (a.preisart ? (PREISARTTEXT[a.preisart] ?? a.preisart) : 'offen'),
      zeige: (a) =>
        a.preisart ? (
          PREISARTTEXT[a.preisart] ?? a.preisart
        ) : (
          <span className="stumm">offen</span>
        ),
    },
    {
      schluessel: 'betrag',
      text: 'Betrag',
      rechts: true,
      zahl: true,
      wert: (a) => String(betrag(a) ?? ''),
      sortierwert: (a) => betrag(a),
      zeige: (a) => {
        const summe = betrag(a);
        if (summe !== null) return euro(summe);
        // Beim Stundensatz gibt es bewusst keine Zahl — die Stunden stehen am
        // Arbeitstag des Fahrers, nicht am Auftrag. Eine geratene Zahl wäre
        // hier schlimmer als ein Strich.
        return (
          <span className="stumm">
            {a.preisart === 'stundensatz' ? 'nach Stunden' : '—'}
          </span>
        );
      },
    },
    {
      schluessel: 'herkunft',
      text: 'Angelegt von',
      auswahl: true,
      wert: (a) => HERKUNFTTEXT[a.herkunft] ?? a.herkunft,
      zeige: (a) => (
        <span className="meta">{HERKUNFTTEXT[a.herkunft] ?? a.herkunft}</span>
      ),
    },
  ];

  return (
    <>
      <Seitenkopf
        titel="Disposition"
        erklaerung="Alle Touren — die vom Büro geplanten und die, die ein Fahrer unterwegs angelegt hat. Der Zustand ergibt sich von selbst; zu entscheiden ist nur, was auf die Rechnung darf. Zeile anklicken für alle Einzelheiten."
        // Ist das Formular offen, steht „Abbrechen" in seiner Karte (HJ-035).
        werkzeuge={
          formularOffen ? undefined : (
            <Knopf art="haupt" onClick={() => setFormularOffen(true)}>
              Tour eintragen
            </Knopf>
          )
        }
      />

      {formularOffen && (
        <div style={{ marginBottom: 16 }}>
          {/* Drei getrennte Wege statt eines überladenen „fertig": Eingetragen
              heißt NICHT geschlossen, und geschlossen heißt nicht abgesprungen.
              Vorher tat ein Aufruf alles drei — und wer fünf Touren nacheinander
              eintrug, landete jedes Mal in der Detailsicht und musste zurück. */}
          <AuftragAnlegen
            schliessen={() => setFormularOffen(false)}
            eingetragen={() => liste.auffrischen()}
            oeffneTour={(id) => gehZu(`/auftrag/${id}`)}
          />
        </div>
      )}

      <Vorausschau zeilen={liste.daten ?? []} />

      <Karte titel="Alle Touren">
        {fehler && (
          <div style={{ marginBottom: 12 }}>
            <Fehlerkasten fehler={fehler} />
          </div>
        )}

        {frage && (
          <div ref={inDenBlick} style={{ marginBottom: 12 }}>
            <Rueckfrage
              titel={`${frage.auftrag.nummer ?? 'Diese Tour'} trotzdem zur Abrechnung freigeben?`}
              bestaetigungstext="Trotzdem freigeben"
              laeuft={laeuft !== null}
              abbrechen={() => setFrage(null)}
              bestaetigen={() => {
                const auftrag = frage.auftrag;
                setFrage(null);
                void mitFehlern(auftrag.id, () => abrechnungFreigeben(auftrag.id));
              }}
            >
              <Freigabeinhalt frage={frage.inhalt} />
              {frage.auftrag.kunde ?? 'Ohne Kunde'} ·{' '}
              {frage.auftrag.ladeort ?? '—'} → {frage.auftrag.entladeort ?? '—'}.
              Zurücknehmen kannst du die Freigabe jederzeit.
            </Rueckfrage>
          </div>
        )}

        {liste.laedt && !liste.daten ? (
          <Ladehinweis />
        ) : liste.fehler ? (
          <Fehlerkasten fehler={liste.fehler} nochmal={liste.auffrischen} />
        ) : (
          <Filtertabelle
            spalten={spalten}
            zeilen={liste.daten ?? []}
            schluessel={(a) => a.id}
            onZeile={(a) => gehZu(`/auftrag/${a.id}`)}
            // Die neueste Tour oben, Touren ohne Nummer („folgt") am Ende (HJ-034).
            anfangssortierung={{ schluessel: 'nummer', ab: true }}
            leer="Noch keine Tour eingetragen."
            zaehlung={(gezeigt, gesamt) =>
              gezeigt === gesamt
                ? `${gesamt} Touren`
                : `${gezeigt} von ${gesamt} Touren`
            }
          />
        )}
      </Karte>
    </>
  );
}

/**
 * Die Zukunftssicht.
 *
 * ═══ WOFÜR DER PREIS BEIM EINTRAGEN GUT IST ═══
 *
 * Wird der Preis erst beim Abrechnen erfasst, kennt das System immer nur die
 * Vergangenheit. Steht er schon beim Eintragen dran, beantwortet diese Leiste
 * die Frage, die im Büro tatsächlich gestellt wird: Was ist gerade an Umsatz
 * unterwegs, und was liegt abrechnungsfertig herum?
 *
 * Bewusst mit Zähler daneben: „12.400 € aus 9 von 14 Touren" ist eine andere
 * Aussage als „12.400 €". Fehlt an fünf Touren der Preis, ist die Summe zu
 * klein — und das muss man sehen, statt es zu glauben.
 *
 * ═══ WARUM DREI FELDER UND NICHT ZWEI ═══
 *
 * Seit die Abrechnung eine Freigabe verlangt, gibt es zwischen „gefahren" und
 * „abrechnungsfertig" einen dritten Stapel: Touren, bei denen alles vorliegt und
 * nur noch niemand entschieden hat. Genau dieser Stapel wächst still an, wenn
 * ihn niemand sieht — er ist der Preis der Freigabe, und deshalb steht er hier.
 */
function Vorausschau(eigenschaften: { zeilen: Auftragszeile[] }) {
  const offen = eigenschaften.zeilen.filter(
    (a) => a.zustand === 'geplant' || a.zustand === 'tour_offen',
  );
  const wartet = eigenschaften.zeilen.filter(
    (a) => a.zustand === 'abrechnungsreif',
  );
  const fertig = eigenschaften.zeilen.filter((a) => a.zustand === 'abrechenbar');

  if (offen.length === 0 && wartet.length === 0 && fertig.length === 0) return null;

  return (
    <div className="vorausschau">
      <Block titel="Noch zu fahren" zeilen={offen} />
      <Block titel="Wartet auf Freigabe" zeilen={wartet} />
      <Block titel="Zur Abrechnung freigegeben" zeilen={fertig} hervor />
    </div>
  );
}

function Block(eigenschaften: {
  titel: string;
  zeilen: Auftragszeile[];
  hervor?: boolean;
}) {
  const mitPreis = eigenschaften.zeilen.filter((a) => betrag(a) !== null);
  const summe = summeAufCent(mitPreis.map((a) => betrag(a) ?? 0));
  const ohnePreis = eigenschaften.zeilen.length - mitPreis.length;

  return (
    <div className={`vorausschau__block${eigenschaften.hervor ? ' vorausschau__block--hervor' : ''}`}>
      <span className="vorausschau__marke">{eigenschaften.titel}</span>
      <strong className="vorausschau__summe">{euro(summe)}</strong>
      <span className="meta">
        {eigenschaften.zeilen.length}{' '}
        {eigenschaften.zeilen.length === 1 ? 'Tour' : 'Touren'}
        {ohnePreis > 0 && ` · bei ${ohnePreis} fehlt der Betrag`}
      </span>
    </div>
  );
}
